/**
 * Demise Dice Theme - Global JavaScript
 * Handles cart functionality, mobile menu, product variants, and UI interactions
 */

(function() {
  'use strict';

  // ===============================
  // Mobile Menu
  // ===============================
  const MobileMenu = {
    init() {
      this.toggle = document.querySelector('.mobile-menu-toggle');
      this.menu = document.getElementById('mobile-menu');
      this.body = document.body;

      if (!this.toggle || !this.menu) return;

      this.toggle.addEventListener('click', () => this.toggleMenu());

      // Close menu when clicking on a link
      this.menu.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => this.closeMenu());
      });

      // Close menu on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.closeMenu();
        }
      });
    },

    toggleMenu() {
      const isOpen = this.isOpen();
      this.toggle.setAttribute('aria-expanded', !isOpen);
      this.menu.classList.toggle('is-open');
      this.toggle.classList.toggle('is-active');
      this.body.classList.toggle('mobile-menu-open');
    },

    closeMenu() {
      this.toggle.setAttribute('aria-expanded', 'false');
      this.menu.classList.remove('is-open');
      this.toggle.classList.remove('is-active');
      this.body.classList.remove('mobile-menu-open');
    },

    isOpen() {
      return this.menu.classList.contains('is-open');
    }
  };

  // ===============================
  // Cart Functionality
  // ===============================
  const Cart = {
    init() {
      this.cartDrawer = document.getElementById('cart-drawer');
      this.cartCount = document.getElementById('cart-count');
      this.cartToggle = document.getElementById('cart-icon-bubble');
      this.cartClose = document.querySelector('.cart-drawer__close');

      this.bindEvents();
    },

    bindEvents() {
      // Add to cart forms
      document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
        form.addEventListener('submit', (e) => this.handleAddToCart(e));
      });

      // Cart drawer toggle
      if (this.cartToggle && this.cartDrawer) {
        this.cartToggle.addEventListener('click', (e) => {
          if (this.cartDrawer) {
            e.preventDefault();
            this.openDrawer();
          }
        });
      }

      // Close cart drawer
      if (this.cartClose) {
        this.cartClose.addEventListener('click', () => this.closeDrawer());
      }

      // Close on overlay click
      if (this.cartDrawer) {
        this.cartDrawer.addEventListener('click', (e) => {
          if (e.target === this.cartDrawer) {
            this.closeDrawer();
          }
        });
      }

      // Quantity buttons
      document.addEventListener('click', (e) => {
        if (e.target.matches('.quantity-btn')) {
          this.handleQuantityChange(e.target);
        }
      });

      // Remove item buttons
      document.addEventListener('click', (e) => {
        if (e.target.matches('.cart-item__remove') || e.target.closest('.cart-item__remove')) {
          e.preventDefault();
          const btn = e.target.closest('.cart-item__remove');
          this.removeItem(btn.dataset.key);
        }
      });
    },

    async handleAddToCart(e) {
      e.preventDefault();
      const form = e.target;
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn.textContent;

      submitBtn.textContent = 'Adding...';
      submitBtn.disabled = true;

      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!response.ok) throw new Error('Add to cart failed');

        const item = await response.json();
        await this.updateCart();
        this.openDrawer();

        submitBtn.textContent = 'Added!';
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 1500);
      } catch (error) {
        console.error('Error adding to cart:', error);
        submitBtn.textContent = 'Error';
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 1500);
      }
    },

    async updateCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Update cart count
        if (this.cartCount) {
          this.cartCount.textContent = cart.item_count;
        }

        // Update all cart counts on page
        document.querySelectorAll('.cart-count').forEach(el => {
          el.textContent = cart.item_count;
        });

        // Update cart drawer content if it exists
        this.updateDrawerContent(cart);

        return cart;
      } catch (error) {
        console.error('Error updating cart:', error);
      }
    },

    updateDrawerContent(cart) {
      const itemsContainer = document.querySelector('.cart-drawer__items');
      const subtotal = document.querySelector('.cart-drawer__subtotal-price');
      const emptyMessage = document.querySelector('.cart-drawer__empty');
      const footer = document.querySelector('.cart-drawer__footer');

      if (cart.item_count === 0) {
        if (itemsContainer) {
          itemsContainer.innerHTML = '';
          itemsContainer.style.display = 'none';
        }
        if (emptyMessage) emptyMessage.style.display = 'block';
        if (footer) footer.style.display = 'none';
        return;
      }

      if (emptyMessage) emptyMessage.style.display = 'none';
      if (footer) footer.style.display = 'block';
      if (itemsContainer) {
        itemsContainer.style.display = 'flex';

        itemsContainer.innerHTML = cart.items.map(item => `
          <div class="cart-item" data-key="${item.key}">
            <div class="cart-item__image">
              <img src="${item.image || ''}" alt="${item.title}" width="80" height="80">
            </div>
            <div class="cart-item__details">
              <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
              <div class="cart-item__price">${this.formatMoney(item.final_line_price)}</div>
              <div class="cart-item__quantity">
                <button class="quantity-btn" data-action="decrease" data-key="${item.key}">-</button>
                <span class="quantity-value">${item.quantity}</span>
                <button class="quantity-btn" data-action="increase" data-key="${item.key}">+</button>
              </div>
            </div>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove item">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        `).join('');
      }

      if (subtotal) {
        subtotal.textContent = this.formatMoney(cart.total_price);
      }
    },

    async handleQuantityChange(btn) {
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const itemEl = btn.closest('.cart-item');
      const quantityEl = itemEl.querySelector('.quantity-value');
      let quantity = parseInt(quantityEl.textContent);

      if (action === 'increase') {
        quantity++;
      } else if (action === 'decrease') {
        quantity = Math.max(0, quantity - 1);
      }

      await this.updateItemQuantity(key, quantity);
    },

    async updateItemQuantity(key, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity })
        });

        await this.updateCart();
      } catch (error) {
        console.error('Error updating quantity:', error);
      }
    },

    async removeItem(key) {
      await this.updateItemQuantity(key, 0);
    },

    openDrawer() {
      if (this.cartDrawer) {
        this.cartDrawer.classList.add('is-open');
        document.body.classList.add('cart-drawer-open');
      }
    },

    closeDrawer() {
      if (this.cartDrawer) {
        this.cartDrawer.classList.remove('is-open');
        document.body.classList.remove('cart-drawer-open');
      }
    },

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // ===============================
  // Product Variants
  // ===============================
  const ProductVariants = {
    init() {
      this.forms = document.querySelectorAll('.product-form');
      this.forms.forEach(form => this.setupForm(form));
    },

    setupForm(form) {
      const selects = form.querySelectorAll('.product-option-select');
      const variantInput = form.querySelector('input[name="id"]');
      const variantsJson = document.getElementById('product-variants-json');

      if (!variantsJson || selects.length === 0) return;

      const variants = JSON.parse(variantsJson.textContent);

      selects.forEach(select => {
        select.addEventListener('change', () => {
          const selectedOptions = Array.from(selects).map(s => s.value);
          const variant = this.findVariant(variants, selectedOptions);

          if (variant) {
            variantInput.value = variant.id;
            this.updatePrice(form, variant);
            this.updateButton(form, variant);
            this.updateUrl(variant);
          }
        });
      });
    },

    findVariant(variants, selectedOptions) {
      return variants.find(variant => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });
    },

    updatePrice(form, variant) {
      const priceEl = form.closest('.product').querySelector('.product-price__regular');
      const comparePriceEl = form.closest('.product').querySelector('.product-price__compare');

      if (priceEl) {
        priceEl.textContent = '$' + (variant.price / 100).toFixed(2);
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = '$' + (variant.compare_at_price / 100).toFixed(2);
          comparePriceEl.style.display = 'inline';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }
    },

    updateButton(form, variant) {
      const btn = form.querySelector('[type="submit"]');
      if (!btn) return;

      if (variant.available) {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      } else {
        btn.disabled = true;
        btn.textContent = 'Sold Out';
      }
    },

    updateUrl(variant) {
      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }
  };

  // ===============================
  // Product Gallery
  // ===============================
  const ProductGallery = {
    init() {
      this.galleries = document.querySelectorAll('.product-gallery');
      this.galleries.forEach(gallery => this.setupGallery(gallery));
    },

    setupGallery(gallery) {
      const mainImage = gallery.querySelector('.product-image-main');
      const thumbnails = gallery.querySelectorAll('.product-thumbnail');

      if (!mainImage || thumbnails.length === 0) return;

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          // Update main image using data-media-url attribute
          const newSrc = thumb.dataset.mediaUrl;
          if (newSrc) {
            mainImage.src = newSrc;
            const thumbImg = thumb.querySelector('img');
            if (thumbImg) {
              mainImage.alt = thumbImg.alt;
            }
          }

          // Update active state
          thumbnails.forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        });
      });
    }
  };

  // ===============================
  // Quantity Selector
  // ===============================
  const QuantitySelector = {
    init() {
      document.addEventListener('click', (e) => {
        if (e.target.matches('.quantity-selector__btn')) {
          this.handleClick(e.target);
        }
      });
    },

    handleClick(btn) {
      const wrapper = btn.closest('.quantity-selector');
      const input = wrapper.querySelector('.quantity-selector__input');
      const action = btn.dataset.action;
      let value = parseInt(input.value);

      if (action === 'increase') {
        value++;
      } else if (action === 'decrease') {
        value = Math.max(1, value - 1);
      }

      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  // ===============================
  // Sticky Header
  // ===============================
  const StickyHeader = {
    init() {
      this.header = document.querySelector('.header');
      if (!this.header) return;

      this.lastScroll = 0;
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    },

    onScroll() {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        this.header.classList.add('is-sticky');
      } else {
        this.header.classList.remove('is-sticky');
      }

      if (currentScroll > this.lastScroll && currentScroll > 200) {
        this.header.classList.add('is-hidden');
      } else {
        this.header.classList.remove('is-hidden');
      }

      this.lastScroll = currentScroll;
    }
  };

  // ===============================
  // Newsletter Form
  // ===============================
  const NewsletterForm = {
    init() {
      const forms = document.querySelectorAll('.newsletter-form');
      forms.forEach(form => {
        form.addEventListener('submit', (e) => this.handleSubmit(e));
      });
    },

    handleSubmit(e) {
      const form = e.target;
      const btn = form.querySelector('button[type="submit"]');
      const originalText = btn.textContent;

      // Let Shopify handle the actual submission
      btn.textContent = 'Subscribing...';

      setTimeout(() => {
        btn.textContent = originalText;
      }, 2000);
    }
  };

  // ===============================
  // Lazy Loading Images
  // ===============================
  const LazyLoad = {
    init() {
      if ('loading' in HTMLImageElement.prototype) {
        // Browser supports native lazy loading
        const images = document.querySelectorAll('img[loading="lazy"]');
        images.forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      } else {
        // Fallback for older browsers
        this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.3.2/lazysizes.min.js');
      }
    },

    loadScript(src) {
      const script = document.createElement('script');
      script.src = src;
      document.body.appendChild(script);
    }
  };

  // ===============================
  // Smooth Scroll
  // ===============================
  const SmoothScroll = {
    init() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const href = anchor.getAttribute('href');
          if (href === '#') return;

          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        });
      });
    }
  };

  // ===============================
  // Initialize
  // ===============================
  document.addEventListener('DOMContentLoaded', () => {
    MobileMenu.init();
    Cart.init();
    ProductVariants.init();
    ProductGallery.init();
    QuantitySelector.init();
    StickyHeader.init();
    NewsletterForm.init();
    LazyLoad.init();
    SmoothScroll.init();
  });

  // Expose Cart to window for external use
  window.DemiseDiceCart = Cart;

})();
